import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { addReaction, fetchReactions, removeReaction } from './reactions';

/**
 * Server state for quick reactions (Drop 3b-2). Kept apart from comments/hooks
 * so a thread that cannot read comment_reactions (migration 181 not run yet)
 * loses only its reaction chips, never its comments.
 */

export const reactionKeys = {
  all: ['comment-reactions'] as const,
  forComments: (commentIds: string[]) => [...reactionKeys.all, ...commentIds] as const,
};

/** Every reaction on these comments, one read for the whole thread. */
export function useCommentReactions(commentIds: string[]) {
  const { client, access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';
  return useQuery({
    queryKey: reactionKeys.forComments(commentIds),
    queryFn: () => fetchReactions(client, commentIds, myId),
    enabled: commentIds.length > 0 && myId !== '',
    retry: false,
  });
}

/** Adds the reaction, or removes it when it is already yours. */
export function useToggleReaction() {
  const { client, access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { commentId: string; emoji: string; mine: boolean }) =>
      input.mine
        ? removeReaction(client, input.commentId, input.emoji, myId)
        : addReaction(client, input.commentId, input.emoji),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: reactionKeys.all });
    },
  });
}
